import React, { Fragment } from 'react';
import { EyeOff, ShieldAlert, UserX, MessageSquareOff, Scale, UtensilsCrossed, Car, Package, ShoppingBag, MoreHorizontal, XCircle } from 'lucide-react';
export function ProblemSlide() {
  const problems = [{
    icon: <EyeOff className="w-5 h-5 text-[#2EBCB0]" strokeWidth={2} data-id="element-427" />,
    title: 'No Central Visibility',
    desc: 'Delivery activities operate across disconnected platforms'
  }, {
    icon: <ShieldAlert className="w-5 h-5 text-[#2EBCB0]" strokeWidth={2} data-id="element-428" />,
    title: 'Uncontrolled Operations',
    desc: 'Limited enforcement on driver identity, vehicle usage, and assignments'
  }, {
    icon: <UserX className="w-5 h-5 text-[#2EBCB0]" strokeWidth={2} data-id="element-429" />,
    title: 'Lack of Accountability',
    desc: 'Difficult to track violations and assign responsibility'
  }, {
    icon: <MessageSquareOff className="w-5 h-5 text-[#2EBCB0]" strokeWidth={2} data-id="element-430" />,
    title: 'Limited Visibility on Delivery Interactions',
    desc: 'No audit trail or monitoring of delivery engagement'
  }, {
    icon: <Scale className="w-5 h-5 text-[#2EBCB0]" strokeWidth={2} data-id="element-431" />,
    title: 'Lack of Unified Operational Standards',
    desc: 'No consistent structure for data, operations, or compliance'
  }];
  const platforms = [{
    name: 'Talabat',
    icon: <img src="https://cdn.magicpatterns.com/uploads/7756UG5Jym81A2wQ5NTmPg/Talabat_Logo.jpg" alt="Talabat" className="w-10 h-10 rounded-lg object-cover" data-id="element-432" />,
    color: 'border-[#F05C2B]/20'
  }, {
    name: 'Careem',
    icon: <img src="https://cdn.magicpatterns.com/uploads/7xRrJrg1q6oHJyA6jgCB5f/careem_logo.jpg" alt="Careem" className="w-10 h-10 rounded-lg object-cover" data-id="element-433" />,
    color: 'border-[#47A23F]/20'
  }, {
    name: 'Amazon',
    icon: <img src="https://cdn.magicpatterns.com/uploads/5ovEx7PeQYHdqRHWJccm3E/amazon_logo.jpg" alt="Amazon" className="w-10 h-10 rounded-lg object-cover" data-id="element-434" />,
    color: 'border-[#232F3E]/20'
  }, {
    name: 'Noon',
    icon: <img src="https://cdn.magicpatterns.com/uploads/tD7MZW9eSLEFkb5TBBWD9M/noon_logo.png" alt="Noon" className="w-10 h-10 rounded-lg object-cover" data-id="element-435" />,
    color: 'border-[#FEEE00]/40'
  }, {
    name: 'Other',
    icon: <MoreHorizontal className="w-6 h-6 text-gray-500" strokeWidth={1.5} data-id="element-436" />,
    color: 'border-gray-200 bg-gray-50'
  }];
  return <div className="flex w-screen h-screen overflow-hidden bg-white text-[#1a1a2e]" data-id="element-437">
      {/* Left Panel - 45% */}
      <div className="w-[45%] h-full flex flex-col px-16 py-12 z-10 relative shrink-0 border-r border-gray-100" data-id="element-438">
        {/* Top Header: Logo & Slide Number */}
        <div className="flex justify-between items-start mb-12" data-id="element-439">
          <img src="https://cdn.magicpatterns.com/uploads/wGUVVF9ppu2J6VxJ2kpoN3/Pair2_Logo.png" alt="Pair2 Logo" className="h-10 w-auto object-contain" data-id="element-440" />
        </div>

        {/* Main Content Area */}
        <div className="flex-grow flex flex-col justify-center" data-id="element-441">
          <span className="text-sm font-semibold text-[#2EBCB0] uppercase tracking-widest mb-3" data-id="element-442">
            The Problem
          </span>
          <h2 className="text-[2rem] leading-[1.2] font-bold text-[#1a1a2e] mb-10 max-w-[90%]" data-id="element-443">
            A Fragmented Delivery Ecosystem with Limited Control
          </h2>

          <div className="flex flex-col gap-6" data-id="element-444">
            {problems.map((item, index) => <div key={index} className="flex items-start gap-4" data-id="element-445">
                <div className="mt-1 shrink-0 p-2 bg-[#2EBCB0]/10 rounded-lg" data-id="element-446">
                  {item.icon}
                </div>
                <div data-id="element-447">
                  <h3 className="font-semibold text-[#1a1a2e] text-[1.0625rem] mb-0.5" data-id="element-448">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-[0.9375rem] leading-snug max-w-[95%]" data-id="element-449">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Bottom Quote */}
        <div className="mt-8 pl-4 border-l-2 border-[#2EBCB0]" data-id="element-450">
          <p className="text-gray-500 italic text-[0.9375rem] leading-relaxed" data-id="element-451">
            "Delivery operations today function in silos — without a unified
            control framework."
          </p>
        </div>
      </div>

      {/* Right Panel - 55% */}
      <div className="w-[55%] h-full bg-[#f8f9fb] flex flex-col items-center justify-center relative p-12" data-id="element-452">
        {/* Diagram Container */}
        <div className="w-full max-w-4xl flex flex-col items-center relative" data-id="element-453">
          {/* Top Row: Platforms + Broken Connections */}
          <div className="flex justify-between w-full mb-8 relative z-10 px-4" data-id="element-454">
            {platforms.map((platform, index) => <div key={index} className="flex flex-col items-center w-24 relative" data-id="element-455">
                {/* Platform Card */}
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border ${platform.color} shadow-sm bg-white mb-3 z-10`} data-id="element-456">
                  {platform.icon}
                </div>
                <span className="text-sm font-medium text-gray-700 mb-4" data-id="element-457">
                  {platform.name}
                </span>

                {/* Dashed line going down */}
                <div className="w-px h-16 border-l-2 border-dashed border-gray-300" data-id="element-458"></div>

                {/* Red X mark */}
                <div className="bg-[#f8f9fb] p-1 z-10 -mt-3" data-id="element-459">
                  <XCircle className="w-6 h-6 text-red-500 bg-white rounded-full" strokeWidth={2} data-id="element-460" />
                </div>
              </div>)}
          </div>

          {/* Horizontal Divider Line (Broken) */}
          <div className="w-full h-px border-t-2 border-dashed border-red-200 mb-8 relative" data-id="element-461"></div>

          {/* Bottom Box: No Central Control */}
          <div className="bg-red-50 border border-red-200 rounded-xl px-8 py-5 flex items-center gap-3 shadow-sm w-[80%] justify-center relative z-10" data-id="element-462">
            <ShieldAlert className="w-6 h-6 text-red-500 shrink-0" strokeWidth={2} data-id="element-463" />
            <span className="text-red-700 font-semibold text-lg tracking-tight" data-id="element-464">
              No Central Monitoring or Control
            </span>
          </div>

          {/* Decorative background elements for diagram */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-50 to-transparent opacity-50 pointer-events-none -z-10" data-id="element-465"></div>
        </div>
      </div>
    </div>;
}